import type { Utms } from '../utm';
import { capture } from '../analytics/posthog';

/**
 * Pageviews are captured natively by PostHog (capture_pageview:'history_change'
 * in src/lib/analytics/posthog.ts), so there is nothing to do here per route.
 * Kept as a no-op so usePageTracking has a stable seam and re-adding a manual
 * pageview later is a one-line change. (Previously pushed to the GTM dataLayer;
 * GTM was removed 2026-07-21 — see index.html.)
 */
export function trackPageView(_url: string): void {
    /* no-op — PostHog auto-captures SPA pageviews */
}

export function trackSignup(method: 'email' | 'google', utms: Utms): void {
    if (typeof window === 'undefined') return;
    capture('signup_completed', { method, ...utms });
}

export function trackBlogFeedback(slug: string, helpful: boolean): void {
    if (typeof window === 'undefined') return;
    capture('blog_feedback', { slug, helpful });
}

export function trackCalculatorFeedback(calculator: string, helpful: boolean): void {
    if (typeof window === 'undefined') return;
    capture('calculator_feedback', { calculator, helpful });
}

export function trackCreatorFeedback(context: string, answer: string, detail?: string): void {
    if (typeof window === 'undefined') return;
    capture('creator_feedback', { context, answer, ...(detail ? { detail } : {}) });
}

export function trackEmailCapture(context: string, email: string): void {
    if (typeof window === 'undefined') return;
    capture('email_captured', { context, email });
}

export function trackCheckoutStarted(creatorId: string): void {
    if (typeof window === 'undefined') return;
    capture('checkout_started', { creatorId });
}

export function trackMissionPageFeedback(creatorId: string, helpful: boolean): void {
    if (typeof window === 'undefined') return;
    capture('mission_page_feedback', { creatorId, helpful });
}

export type DonationAbandonReason = 'notReadyYet' | 'wantedMoreInfo' | 'askUnclear' | 'somethingBroken' | 'dismissed';

// Ported from sportfund's TSF-283 (commit bb045a2): same event name, same
// reason taxonomy, (reason, creatorId) argument order kept identical so the
// two products' PostHog data stays comparable.
export function trackDonationAbandonFeedback(reason: DonationAbandonReason, creatorId: string): void {
    if (typeof window === 'undefined') return;
    capture('donation_abandon_feedback', { reason, creatorId });
}

// MSF-295: checkoutAttemptedRef flips true the instant a donor clicks
// Give/Subscribe (see DonationForm.tsx), before the create*Checkout call
// returns -- so a donor whose attempt throws (e.g. "Creator account is not
// charge-enabled yet") gets no DonationAbandonFeedback prompt at all; that
// gate only catches someone who closed the dialog WITHOUT attempting. This
// is the only signal we have on that drop, since a failed attempt is
// anonymous (no email collected yet) and can't be followed up by contacting
// the donor.
export function trackCheckoutFailed(reason: string, creatorId: string): void {
    if (typeof window === 'undefined') return;
    capture('checkout_failed', { reason, creatorId });
}

// Ported from sportfund's TSF-331: checkout_started -> purchase never had a
// feedback prompt, unlike donate_ask_viewed -> checkout_started above. Fires
// when a donor comes back via cancelUrl from Stripe's hosted checkout instead
// of completing payment. Same event name/reason taxonomy as sportfund so the
// two products' PostHog data stays comparable.
export type CheckoutCancelReason = 'priceTooHigh' | 'paymentMethodIssue' | 'changedMyMind' | 'somethingBroken';

export function trackCheckoutCancelFeedback(reason: CheckoutCancelReason, creatorId: string, frequencyType?: string): void {
    if (typeof window === 'undefined') return;
    capture('checkout_cancel_feedback', { reason, creatorId, frequencyType });
}
